import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import type { ZohoPricingConfigDTO } from "../../data/onboardingApi";
import { fetchZohoPricing } from "../../data/onboardingApi";
import { planLabel } from "../../domain/zohoSizing";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { ChosenPlanSummary, SizingPendingCard } from "../../components/ChosenPlanSummary";
import { DecisionCardOption } from "../../components/DecisionCards";
import { QuestionCard } from "../../components/QuestionCard";
import { SetupChecklist, SetupChecklistItem } from "../../components/SetupChecklist";
import { SubStepShell } from "../../components/SubStepShell";
import { VideoPlaceholder } from "../../components/VideoPlaceholder";
import { useSubStepNav } from "../useSubStepNav";

const ZOHO_ACCOUNT_OPTIONS: DecisionCardOption[] = [
  {
    value: "yes",
    title: "Yes, this client already has Zoho Books",
    description: "We'll connect to the existing organisation and books.",
  },
  {
    value: "no",
    title: "No, this client needs one created",
    description: "We'll guide you through creating a new Zoho organisation, then set up fresh books.",
  },
];

const ANSWER_KEY = "zohoAccount";

interface ZohoAccountAnswer {
  hasZohoAccount?: boolean;
}

/**
 * Creating the account. The plan item comes first and names the chosen plan,
 * because signing up on a lower one is the mistake that is expensive to undo:
 * the organisation has to exist before it can be upgraded, and by then the CA
 * has already moved on to the next step.
 */
function noAccountChecklist(chosenPlanLabel: string | undefined): SetupChecklistItem[] {
  return [
    {
      id: "sign-up-on-plan",
      label: chosenPlanLabel
        ? `Sign up on ${chosenPlanLabel} or any plan above it. Anything below won't support this client's setup.`
        : "Sign up on the plan we recommend or any plan above it, once package sizing is complete.",
      hint: "Choose this plan during Zoho Books signup.",
    },
    {
      id: "create-account",
      label: chosenPlanLabel
        ? `Create the Zoho account on the ${chosenPlanLabel} plan you selected`
        : "Create a Zoho account",
      hint: "Sign up at zoho.com/books if you don't have one already.",
    },
    {
      id: "create-org",
      label: "Create a Zoho Books organisation",
      hint: "Set up the organisation for this client inside Zoho.",
    },
    {
      id: "return-here",
      label: "Return here to continue",
      hint: "Come back once the organisation is ready.",
    },
  ];
}

/**
 * Checking an account that already exists against what package sizing asked
 * for. Informational, like the create-account list: an upgrade is a
 * before-go-live task, and blocking the rest of onboarding on a billing
 * change the CA may need the client to approve would stall everything else.
 */
function hasAccountChecklist(chosenPlanLabel: string | undefined): SetupChecklistItem[] {
  return [
    {
      id: "check-current-plan",
      label: "Check which Zoho Books plan this client is on today.",
      hint: "Look under Settings → Subscription in their Zoho Books account.",
    },
    {
      id: "compare-to-recommendation",
      label: chosenPlanLabel
        ? `Compare it against our recommendation: ${chosenPlanLabel}.`
        : "Compare it against our recommendation, once package sizing is complete.",
      hint: "See the recommendation above.",
    },
    {
      id: "upgrade-if-lower",
      label: chosenPlanLabel
        ? `If they're on a lower plan, upgrade to ${chosenPlanLabel} or above before go-live.`
        : "If they're on a lower plan than we recommend, upgrade before go-live.",
      hint: "Upgrades can be done anytime from inside Zoho Books.",
    },
  ];
}

export function ZohoAccountStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "zoho-account");
  const answer = useCoAnswer<ZohoAccountAnswer>(clientId, ANSWER_KEY) ?? {};

  const [config, setConfig] = useState<ZohoPricingConfigDTO | undefined>();

  useEffect(() => {
    let cancelled = false;
    fetchZohoPricing()
      .then((loaded) => {
        if (!cancelled) setConfig(loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!client) return null;

  // The label lives in onboarding_zoho_plans, so it's looked up rather than
  // showing the raw id. Until the config loads there is no label to name, and
  // both checklists fall back to their generic wording rather than printing an
  // id at the operator.
  const sizing = client.zohoSizing;
  const chosenPlan = sizing?.chosenPlan;
  const chosenPlanLabel = chosenPlan && config ? planLabel(config, chosenPlan) : undefined;

  const noAccountItems = noAccountChecklist(chosenPlanLabel);
  const hasAccountItems = hasAccountChecklist(chosenPlanLabel);

  const showNoAccountChecklist = answer.hasZohoAccount === false;
  const showHasAccountChecklist = answer.hasZohoAccount === true;

  // The lists are informational — points the CA works through in Zoho, not
  // boxes to tick — so the only gate is answering the question itself.
  const canContinue = answer.hasZohoAccount !== undefined;

  return (
    <SubStepShell
      title="Connecting Your Zoho Account"
      description={`Confirm whether ${client.name || "this client"} already has a Zoho Books account.`}
      thenWhat="Continue → Connect Zournal to Zoho Books."
      onContinue={() => void goNext()}
      continueDisabled={!canContinue}
    >
      <QuestionCard
        headline="Does this client already have a Zoho Books account?"
        whyLine="We need to know whether to connect to existing books or help you set up a new Zoho organisation."
        options={ZOHO_ACCOUNT_OPTIONS}
        selected={answer.hasZohoAccount === undefined ? undefined : answer.hasZohoAccount ? "yes" : "no"}
        onSelect={(value) =>
          void persistAnswer<ZohoAccountAnswer>(clientId, ANSWER_KEY, { hasZohoAccount: value === "yes" })
        }
      />

      {config && chosenPlan && sizing ? (
        <ChosenPlanSummary
          clientId={clientId}
          config={config}
          chosenPlan={chosenPlan}
          userCount={sizing.userCount}
        />
      ) : (
        <SizingPendingCard clientId={clientId} />
      )}

      {showHasAccountChecklist && (
        <SetupChecklist title="Finish this checklist before continuing" items={hasAccountItems} />
      )}

      {showNoAccountChecklist && (
        <>
          {/* Official Zoho Books video: "How to Sign Up and Getting Started". */}
          <VideoPlaceholder caption="How to create a Zoho account" videoId="Tl6KIs5gGc4" />
          <SetupChecklist title="Setting up the Zoho account" items={noAccountItems} />
        </>
      )}
    </SubStepShell>
  );
}
